import { useState } from "react";
import { MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCompanySettings } from "@/hooks/use-company-settings";
import { toast } from "sonner";
import { z } from "zod";

const whatsappFormSchema = z.object({
  name: z.string().trim().min(2, "Nome deve ter pelo menos 2 caracteres").max(100, "Nome muito longo"),
  email: z.string().trim().email("E-mail inválido").max(255, "E-mail muito longo"),
  phone: z.string().trim().max(20, "Telefone muito longo").optional().or(z.literal("")),
});

interface WhatsAppFloatProps {
  systemName?: string;
  systemCode?: string;
}

export function WhatsAppFloat({ systemName, systemCode = 'portal' }: WhatsAppFloatProps) {
  const { data: companySettings, isLoading } = useCompanySettings();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: ""
  });
  
  const rawPhone = companySettings?.telefone || (companySettings as any)?.phone;
  const digits = rawPhone?.replace(/\D/g, '') || '';

  if (isLoading) return null;
  if (!digits || digits.length < 10) return null;

  const getSystemMessage = () => {
    switch (systemName) {
      case 'FleetONE':
        return 'Olá! Gostaria de saber mais sobre o FleetONE.';
      case 'TrackingONE':
        return 'Olá! Gostaria de saber mais sobre o TrackingONE.';
      case 'NoteONE':
        return 'Olá! Gostaria de saber mais sobre o NoteONE.';
      default:
        return 'Olá! Gostaria de saber mais sobre o PortalONE.';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validar dados no client antes de prosseguir
      const validatedData = whatsappFormSchema.parse(formData);
      
      console.log('WhatsApp contact:', {
        name: validatedData.name,
        email: validatedData.email,
        systemCode,
        hasPhone: !!validatedData.phone,
      });

      // Lead tracking system discontinued - just open WhatsApp
      toast.success('Abrindo WhatsApp...');

      // Abrir WhatsApp
      const waNumber = digits.startsWith('55') ? digits : `55${digits}`;
      const message = encodeURIComponent(getSystemMessage());
      const whatsappUrl = `https://wa.me/${waNumber}?text=${message}`;
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');

      setOpen(false);
      setFormData({ name: "", email: "", phone: "" });
      
    } catch (error) {
      console.error('WhatsApp form error:', error);
      
      // Validação com Zod
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
        setLoading(false);
        return;
      }
      
      // Mesmo com erro, abrir WhatsApp
      const waNumber = digits.startsWith('55') ? digits : `55${digits}`;
      const message = encodeURIComponent(getSystemMessage());
      const whatsappUrl = `https://wa.me/${waNumber}?text=${message}`;
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
      
      toast.warning('Abrindo WhatsApp.');
      setOpen(false);
      setFormData({ name: "", email: "", phone: "" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110 animate-pulse"
        aria-label="Falar no WhatsApp"
      >
        <MessageCircle size={24} />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Falar no WhatsApp</DialogTitle>
            <DialogDescription>
              Para melhor atendê-lo, por favor informe seus dados.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="wa-name">Nome *</Label>
              <Input
                id="wa-name"
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Seu nome"
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="wa-email">E-mail *</Label>
              <Input
                id="wa-email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="seu@email.com"
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="wa-phone">Telefone (opcional)</Label>
              <Input
                id="wa-phone"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="(00) 00000-0000"
                disabled={loading}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1" disabled={loading}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} className="flex-1 bg-green-500 hover:bg-green-600">
                {loading ? 'Enviando...' : 'Continuar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
